import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle, Clock, User } from 'lucide-react';
import { useTicket } from '../hooks';
import { TicketMessageList, TicketMessageInput } from '../components';
import { ARCHE_PATH } from '../constants';

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { ticket, messages, loading, error, addMessage, refresh } = useTicket(ticketId);

  const statusVariant = (status: string) => {
    switch (status) {
      case 'open':
        return 'default' as const;
      case 'in_progress':
        return 'secondary' as const;
      case 'resolved':
        return 'outline' as const;
      case 'closed':
        return 'outline' as const;
      default:
        return 'default' as const;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Ticket not found'}</p>
            <Link to={`${ARCHE_PATH}/tickets`} className="mt-4 inline-block">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to My Tickets
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSendMessage = async (content: string) => {
    await addMessage(content);
    await refresh();
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Back navigation */}
      <Link to={`${ARCHE_PATH}/tickets`}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Tickets
        </Button>
      </Link>

      {/* Ticket Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <CardTitle className="text-xl">{ticket.subject}</CardTitle>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {new Date(ticket.createdAt).toLocaleString()}
                </div>
                {ticket.assignedTo && (
                  <div className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    {ticket.assignedTo}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusVariant(ticket.status)}>{ticket.status}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground whitespace-pre-wrap">{ticket.description}</p>
        </CardContent>
      </Card>

      {/* Message Thread */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Conversation</h2>
        <TicketMessageList messages={messages} />
        {ticket.status !== 'closed' && <TicketMessageInput onSend={handleSendMessage} />}
        {ticket.status === 'closed' && (
          <Card>
            <CardContent className="py-4 text-center">
              <p className="text-sm text-muted-foreground">
                This ticket is closed. Open a new ticket if you need further assistance.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
